import {
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { CheckCheck, ChevronLeft, Eye, EyeOff, Search, X } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/modern/components/ui/dialog";
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { cn } from "@/modern/utils";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys.jsx";
import { useDebounce } from "@/react/hooks/useDebounce";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";

import {
  NOTIFICATION_POPUP_DEBOUNCE_MS,
  NOTIFICATION_POPUP_MAX_HEIGHT_CLS,
  NOTIFICATION_POPUP_WIDTH_CLS,
  SURFACE_BACK_BTN_CLS,
  SURFACE_ICON_BTN_CLS,
  SURFACE_SEARCH_CLS,
} from "../constants/layout";
import { useNotificationPopup } from "../hooks/useNotificationPopup";
import { useNotificationScope } from "../hooks/useNotificationScope";
import type {
  NotificationFilter,
  NotificationSurfaceMode,
} from "../types";
import { NotificationAllView } from "./NotificationAllView";
import { NotificationAskView } from "./NotificationAskView";
import { NotificationFilterSelector } from "./NotificationFilterSelector";
import { NotificationNotableView } from "./NotificationNotableView";
import { NotificationSummaryView } from "./NotificationSummaryView";

interface NotificationPopupProps {
  record?: unknown;
  onClose: () => void;
  editorInstance?: unknown;
  isShow?: boolean;
}

export function NotificationPopup({
  onClose,
  isShow = true,
}: NotificationPopupProps) {
  if (!isShow) return null;
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className={cn(
          "z-[--swivu-bryntum-z-index] flex flex-col gap-0 overflow-hidden border-border/60 bg-background p-0 shadow-2xl sm:rounded-[2rem]",
          NOTIFICATION_POPUP_WIDTH_CLS,
          NOTIFICATION_POPUP_MAX_HEIGHT_CLS,
        )}
        showCloseButton={false}
        dialogOverlayClassName="z-[--swivu-bryntum-z-index]"
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogTitle className="sr-only">Updates</DialogTitle>
        <DialogDescription className="sr-only">
          Activity across Swivu: notable updates, notifications, and system operations.
        </DialogDescription>
        <TooltipProvider delayDuration={200}>
          <NotificationPopupContent onClose={onClose} />
        </TooltipProvider>
      </DialogContent>
    </Dialog>
  );
}

function NotificationPopupContent({ onClose }: { onClose: () => void }) {
  const { isAccountView } = useNotificationScope();
  const { userPreferences } = useSwivuBryntumContext();
  const [view, setView] = useState<NotificationSurfaceMode>({ mode: "summary" });
  const [filter, setFilter] = useState<NotificationFilter>({ kind: "all" });
  const [searchInput, setSearchInput] = useState("");
  const [hideSeen, setHideSeen] = useState(
    () =>
      userPreferences?.getSetting(
        PERSISTENCE_STATE_KEYS.notificationUnreadOnly,
        "true",
      ) === "true",
  );
  const debouncedSearch = useDebounce(searchInput, NOTIFICATION_POPUP_DEBOUNCE_MS) as string;

  const activeFilter: NotificationFilter =
    view.mode === "all" ? view.filter
    : view.mode === "notable" ? { kind: "notable" }
    : filter;

  const popup = useNotificationPopup({
    filter: activeFilter,
    searchQuery: view.mode === "all" || view.mode === "notable" ? debouncedSearch : "",
    hideSeen,
    // Summary is counts-only; list/notable paginate on scroll.
    listEnabled: view.mode === "all" || view.mode === "notable",
    closePopup: onClose,
  });

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const handleHideSeenChange = useCallback(
    (checked: boolean) => {
      setHideSeen(checked);
      userPreferences?.updateSetting(
        PERSISTENCE_STATE_KEYS.notificationUnreadOnly,
        String(checked),
      );
    },
    [userPreferences],
  );

  const openAll = useCallback(
    (newFilter: NotificationFilter, label?: string) => {
      setFilter(newFilter);
      setSearchInput("");
      setView({ mode: "all", filter: newFilter, label });
    },
    [],
  );

  const selectFilter = useCallback((newFilter: NotificationFilter) => {
    setFilter(newFilter);
    setSearchInput("");
    if (newFilter.kind === "notable") {
      setView({ mode: "notable", index: 0 });
    } else {
      setView({ mode: "all", filter: newFilter });
    }
  }, []);

  const unreadCount = popup.summary.unread;
  const [isMarkingAll, setIsMarkingAll] = useState(false);

  const handleMarkAllSeen = useCallback(async () => {
    if (!popup.hasUnread || isMarkingAll) return;
    setIsMarkingAll(true);
    try {
      await popup.markAllSeen();
    } finally {
      setIsMarkingAll(false);
    }
  }, [isMarkingAll, popup.hasUnread, popup.markAllSeen]);

  const hideSeenLabel = hideSeen ? "Show seen" : "Hide seen";

  const headerActions = (
    <div className="flex shrink-0 items-center gap-1.5">
      <button
        type="button"
        className={cn(SURFACE_BACK_BTN_CLS, "shrink-0")}
        onClick={() => handleHideSeenChange(!hideSeen)}
        aria-pressed={hideSeen}
        aria-label={hideSeenLabel}
      >
        {hideSeen ? (
          <Eye className="size-4 shrink-0" aria-hidden />
        ) : (
          <EyeOff className="size-4 shrink-0" aria-hidden />
        )}
        <span className="whitespace-nowrap">{hideSeenLabel}</span>
      </button>
      <button
        type="button"
        className={cn(
          SURFACE_BACK_BTN_CLS,
          "shrink-0",
          (!popup.hasUnread || isMarkingAll) &&
            "cursor-not-allowed text-muted-foreground/50 hover:border-input hover:text-muted-foreground/50",
        )}
        disabled={!popup.hasUnread || isMarkingAll}
        onClick={() => { void handleMarkAllSeen(); }}
        aria-label={
          unreadCount > 0
            ? `Mark all seen (${unreadCount} unread)`
            : "All seen"
        }
      >
        <CheckCheck className="size-4 shrink-0" aria-hidden />
        <span className="whitespace-nowrap">
          {popup.hasUnread ? "Mark all seen" : "All seen"}
        </span>
      </button>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={cn(SURFACE_ICON_BTN_CLS, "inline-flex items-center justify-center")}
            onClick={onClose}
            aria-label="Close"
          >
            <X className="size-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom">Close</TooltipContent>
      </Tooltip>
    </div>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {view.mode !== "summary" ? (
        <CommandRail
          view={view}
          searchQuery={searchInput}
          onSearchChange={setSearchInput}
          onBackToSummary={() => {
            setSearchInput("");
            setFilter({ kind: "all" });
            setView({ mode: "summary" });
          }}
          activeFilter={activeFilter}
          mappedCounts={popup.mappedCounts}
          isAccountView={isAccountView}
          onSelectFilter={selectFilter}
          headerActions={headerActions}
        />
      ) : null}
      <div
        className={cn(
          "min-h-0 flex-1",
          view.mode === "summary"
            ? "overflow-y-auto"
            : "flex flex-col overflow-hidden",
        )}
      >
        {view.mode === "summary" ? (
          popup.isLoading ? (
            <NotificationSummarySkeleton headerActions={headerActions} />
          ) : (
            <NotificationSummaryView
              summary={popup.summary}
              onOpenNotable={() => setView({ mode: "notable", index: 0 })}
              onOpenAll={openAll}
              onOpenAsk={() => setView({ mode: "ask" })}
              closeButton={headerActions}
            />
          )
        ) : null}
        {view.mode === "notable" ? (
          <NotificationNotableView
            notifications={popup.notifications}
            context={popup.context}
            index={view.index}
            onIndex={(index) => setView({ mode: "notable", index })}
            hasNextPage={popup.hasNextPage}
            isFetchingNextPage={popup.isFetchingNextPage}
            isLoading={popup.isLoading}
            onFetchNextPage={() => { void popup.fetchNextPage(); }}
            onOpenAll={() => openAll({ kind: "all" })}
          />
        ) : null}
        {view.mode === "all" ? (
          <NotificationAllView
            notifications={popup.notifications}
            context={popup.context}
            hasNextPage={popup.hasNextPage}
            isFetchingNextPage={popup.isFetchingNextPage}
            isLoading={popup.isLoading}
            onFetchNextPage={() => { void popup.fetchNextPage(); }}
            onClearFilter={() => selectFilter({ kind: "all" })}
            hasActiveFilter={Boolean(debouncedSearch) || activeFilter.kind !== "all"}
          />
        ) : null}
        {view.mode === "ask" ? (
          <NotificationAskView
            prose={popup.summary.askProse}
            summary={popup.summary}
            mappedCounts={popup.mappedCounts}
          />
        ) : null}
      </div>
    </div>
  );
}

function NotificationSummarySkeleton({
  headerActions,
}: {
  headerActions: ReactNode;
}) {
  const shimmer =
    "relative overflow-hidden rounded-xl bg-accent/40 before:absolute before:inset-0 before:-translate-x-full before:animate-shimmer before:bg-gradient-to-r before:from-transparent before:via-background/60 before:to-transparent";
  return (
    <div className="px-5 pb-6 pt-5 sm:px-7">
      <header className="mb-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className={cn(shimmer, "h-12 w-64 rounded-2xl")} />
          </div>
          {headerActions}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className={cn(shimmer, "h-9 w-28 rounded-full")} />
          <div className={cn(shimmer, "h-9 w-28 rounded-full")} />
          <div className={cn(shimmer, "h-9 w-28 rounded-full")} />
        </div>
      </header>

      <div className={cn(shimmer, "mb-5 h-24 rounded-[1.5rem]")} />

      <div className="grid grid-cols-3 gap-3">
        <div className={cn(shimmer, "col-span-2 row-span-2 h-44 rounded-2xl")} />
        <div className={cn(shimmer, "col-span-1 row-span-2 h-44 rounded-2xl")} />
        <div className={cn(shimmer, "col-span-1 h-28 rounded-2xl")} />
        <div className={cn(shimmer, "col-span-1 h-28 rounded-2xl")} />
        <div className={cn(shimmer, "col-span-1 h-28 rounded-2xl")} />
        <div className={cn(shimmer, "col-span-1 row-span-2 h-44 rounded-2xl")} />
        <div className={cn(shimmer, "col-span-1 row-span-2 h-44 rounded-2xl")} />
        <div className={cn(shimmer, "col-span-1 h-28 rounded-2xl")} />
      </div>
    </div>
  );
}

function CommandRail({
  view,
  searchQuery,
  onSearchChange,
  onBackToSummary,
  activeFilter,
  mappedCounts,
  isAccountView,
  onSelectFilter,
  headerActions,
}: {
  view: NotificationSurfaceMode;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onBackToSummary: () => void;
  activeFilter: NotificationFilter;
  mappedCounts: import("../types").MappedNotificationCounts;
  isAccountView: boolean;
  onSelectFilter: (filter: NotificationFilter) => void;
  headerActions: ReactNode;
}) {
  const showControls = view.mode === "all" || view.mode === "notable";
  return (
    <div className="flex shrink-0 items-center gap-2.5 px-5 pb-3 pt-5 sm:px-7">
      <button type="button" onClick={onBackToSummary} className={SURFACE_BACK_BTN_CLS}>
        <ChevronLeft className="size-4" aria-hidden />
        Summary
      </button>
      {showControls ? (
        <div className="relative min-w-[10rem] flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground/60"
            aria-hidden
          />
          <input
            value={searchQuery}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search this view…"
            aria-label="Search this view"
            className={SURFACE_SEARCH_CLS}
          />
          {searchQuery ? (
            <button
              type="button"
              onClick={() => onSearchChange("")}
              aria-label="Clear search"
              className="absolute right-1.5 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="size-3.5" aria-hidden />
            </button>
          ) : null}
        </div>
      ) : (
        <div className="min-w-0 flex-1" />
      )}
      {showControls ? (
        <NotificationFilterSelector
          activeFilter={activeFilter}
          mappedCounts={mappedCounts}
          isAccountView={isAccountView}
          onSelectFilter={onSelectFilter}
        />
      ) : null}
      {headerActions}
    </div>
  );
}
